import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ProductPicker } from './ProductPicker';
import { api, formatPrice, type CurrentUser, type UnlinkedListing } from '../lib/api';

/**
 * The unlinked review queue.
 *
 * Exact SKU matches never appear here — they link automatically during sync.
 * What remains is exactly the set a human has to judge, so each item is shown
 * beside its top candidates with the reasons that produced the score. A
 * suggestion you cannot explain is a suggestion you cannot trust.
 */

export function UnlinkedQueue({ user }: { user: CurrentUser }) {
  const queryClient = useQueryClient();
  const [toast, setToast] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const canEdit = user.role === 'admin' || user.role === 'editor';

  const [includeInactive, setIncludeInactive] = useState(false);
  const { data, isLoading, error } = useQuery({
    queryKey: ['unlinked', includeInactive],
    queryFn: () => api.unlinked(includeInactive),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['unlinked'] });
    void queryClient.invalidateQueries({ queryKey: ['products'] });
  };

  const linkMutation = useMutation({
    mutationFn: ({ listingId, productId }: { listingId: string; productId: string }) =>
      api.link(listingId, productId),
    onSuccess: () => {
      setToast({ kind: 'ok', text: 'Linked' });
      refresh();
    },
    onError: (err: Error) => setToast({ kind: 'error', text: err.message }),
  });

  const dismissMutation = useMutation({
    mutationFn: ({ listingId, productId }: { listingId: string; productId: string }) =>
      api.dismissSuggestion(listingId, productId),
    onSuccess: () => {
      setToast({ kind: 'ok', text: 'Suggestion dismissed; it will not resurface' });
      refresh();
    },
    onError: (err: Error) => setToast({ kind: 'error', text: err.message }),
  });

  if (isLoading) return <div className="empty-state">Loading…</div>;
  if (error) return <div className="alert error">{(error as Error).message}</div>;

  const listings = data?.listings ?? [];

  return (
    <div>
      <div className="toolbar">
        <strong>{data?.total ?? 0}</strong>
        <span className="muted">
          listing(s) not yet linked to a parent product
        </span>
        <div className="spacer" />
        {/* Withheld listings are not work to do now, so they are left out by
            default — but never silently. */}
        {!includeInactive && (data?.inactiveHidden ?? 0) > 0 && (
          <span className="small muted">
            {data?.inactiveHidden} inactive hidden{' · '}
            <button className="link small" onClick={() => setIncludeInactive(true)}>
              show them
            </button>
          </span>
        )}
        {includeInactive && (
          <button className="link small" onClick={() => setIncludeInactive(false)}>
            hide inactive listings
          </button>
        )}
      </div>

      {toast && (
        <div className={`alert ${toast.kind}`} onClick={() => setToast(null)}>
          {toast.text}
        </div>
      )}

      {listings.length === 0 ? (
        <div className="empty-state">
          <p>Nothing to review.</p>
          <p className="small">
            {(data?.inactiveHidden ?? 0) > 0
              ? `Every listing a channel is currently showing is linked. ${data?.inactiveHidden} inactive one(s) are hidden.`
              : 'Every marketplace listing is linked to a parent product.'}
          </p>
        </div>
      ) : (
        listings.map((listing) => (
          <QueueItem
            key={listing.id}
            listing={listing}
            canEdit={canEdit}
            onLink={(productId) => linkMutation.mutate({ listingId: listing.id, productId })}
            onDismiss={(productId) => dismissMutation.mutate({ listingId: listing.id, productId })}
            busy={linkMutation.isPending || dismissMutation.isPending}
          />
        ))
      )}
    </div>
  );
}

function QueueItem({
  listing, canEdit, onLink, onDismiss, busy,
}: {
  listing: UnlinkedListing;
  canEdit: boolean;
  onLink: (productId: string) => void;
  onDismiss: (productId: string) => void;
  busy: boolean;
}) {
  const [searching, setSearching] = useState(false);

  return (
    <div className="queue-item">
      <div className="queue-head">
        {listing.imageUrl ? (
          <img className="thumb" src={listing.imageUrl} alt="" loading="lazy" />
        ) : (
          <div className="thumb" />
        )}
        <div style={{ flex: 1 }}>
          <div>
            <strong>{listing.title}</strong>{' '}
            <span className="badge">{listing.channelName}</span>
            {listing.status === 'inactive' && (
              <>
                {' '}
                <span className="badge inactive" title="The channel is not currently showing this listing">
                  inactive
                </span>
              </>
            )}
          </div>
          <div className="small muted">
            SKU <span className="sku">{listing.sku ?? 'none'}</span> ·{' '}
            {formatPrice(listing.priceMinor, listing.currency)}
            {listing.url && (
              <>
                {' · '}
                <a href={listing.url} target="_blank" rel="noreferrer">
                  view ↗
                </a>
              </>
            )}
          </div>
        </div>
        {canEdit && (
          <button onClick={() => setSearching((v) => !v)}>
            {searching ? 'Close search' : 'Search…'}
          </button>
        )}
      </div>

      {searching && (
        <ProductPicker listingId={listing.id} actionLabel="Link" busy={busy} onChoose={onLink} />
      )}

      {listing.suggestions.length === 0 ? (
        <p className="small muted">
          No confident suggestions. Use search above to link it by hand.
        </p>
      ) : (
        <div className="candidates">
          {listing.suggestions.map((suggestion) => (
            <div key={suggestion.productId} className="candidate">
              <div>
                <span className="score">{suggestion.score}%</span>{' '}
                <span className="sku">{suggestion.product.skuDisplay}</span>
              </div>
              <div>{suggestion.product.title}</div>
              <ul>
                {suggestion.reasons.map((reason, index) => (
                  <li key={index}>{reason.detail}</li>
                ))}
              </ul>
              {canEdit && (
                <div style={{ display: 'flex', gap: 6 }}>
                  <button
                    className="primary"
                    disabled={busy}
                    onClick={() => onLink(suggestion.productId)}
                  >
                    Link
                  </button>
                  <button disabled={busy} onClick={() => onDismiss(suggestion.productId)}>
                    Not this
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
